import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDriverJobs } from '../../hooks/useDriverJobs';
import JobCard from './JobCard';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPackage, FiCheckCircle, FiRefreshCw } = FiIcons;

export default function DriverDashboard({ showAll = false }) {
  const { profile } = useAuth();
  const { pendingJobs, completedJobs, jobs, loading, error, fetchJobs } = useDriverJobs();
  const [activeFilter, setActiveFilter] = useState('pending');
  const navigate = useNavigate();

  const displayJobs = showAll
    ? (activeFilter === 'pending' ? pendingJobs : completedJobs)
    : pendingJobs;

  return (
    <div className="p-4 pt-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-gray-500 text-sm">Driver Portal</p>
          <h1 className="text-2xl font-bold text-brand-navy">
            {profile?.full_name?.split(' ')[0] || 'Driver'}
          </h1>
        </div>
        <button
          onClick={fetchJobs}
          className="p-2 bg-white rounded-full shadow-sm border border-gray-100 active:scale-95 transition-transform"
        >
          <FiRefreshCw className="text-brand-navy text-lg" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <button
          onClick={() => { setActiveFilter('pending'); navigate('/driver/jobs'); }}
          className="bg-brand-navy rounded-2xl p-4 text-left active:scale-95 transition-transform shadow-premium"
        >
          <FiPackage className="text-brand-gold text-2xl mb-2" />
          <p className="text-3xl font-bold text-white">{pendingJobs.length}</p>
          <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Pending Jobs</p>
        </button>
        <button
          onClick={() => { setActiveFilter('completed'); navigate('/driver/jobs'); }}
          className="bg-white rounded-2xl p-4 text-left border border-gray-100 active:scale-95 transition-transform shadow-sm"
        >
          <FiCheckCircle className="text-green-500 text-2xl mb-2" />
          <p className="text-3xl font-bold text-brand-navy">{completedJobs.length}</p>
          <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Completed</p>
        </button>
      </div>

      {/* Filter Tabs (showAll mode) */}
      {showAll && (
        <div className="flex gap-2 mb-4">
          {['pending', 'completed'].map(f => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${
                activeFilter === f
                  ? 'bg-brand-navy text-white'
                  : 'bg-white text-gray-500 border border-gray-200'
              }`}
            >
              {f === 'pending' ? `Pending (${pendingJobs.length})` : `Completed (${completedJobs.length})`}
            </button>
          ))}
        </div>
      )}

      {/* Jobs List */}
      <h2 className="text-lg font-bold text-brand-navy mb-3">
        {showAll ? '' : 'Upcoming Pickups'}
      </h2>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">
          <SafeIcon name="AlertCircle" className="text-4xl mx-auto mb-3" />
          <p className="font-medium">{error}</p>
          <button onClick={fetchJobs} className="mt-3 text-brand-navy underline text-sm">Retry</button>
        </div>
      ) : displayJobs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
          <SafeIcon name="Inbox" className="text-5xl text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No jobs found</p>
          <p className="text-gray-400 text-sm mt-1">
            {activeFilter === 'pending' ? 'You have no pending pickups.' : 'No completed jobs yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayJobs.map(job => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}